import { Children } from 'react';
import classNames from 'classnames';

import { GRID_LINE_HEIGHT } from '../../../constants';
import { ColumnElement } from '../columns/types';
import { useGridContext } from '../context/GridContext';
import { GridHeaderElement } from '../header/elements';
import { GridHeaderLabel } from '../header/GridHeaderLabel';
import { cellCSS } from '../utils';

import cn from '../Grid.module.scss';

export const GridHeaderStacked = ({ column, index }: { column: ColumnElement; index: number }) => {
  const {
    header_height,
    header_clamp,
    depth,
    cumulative_fixed_right_widths,
    cumulative_fixed_left_widths,
    cumulative_column_widths,
    column_widths,
    filler_required,
    theme,
  } = useGridContext();

  const { label, fixed } = column.props;
  const children = Children.toArray(column.props.children) as ColumnElement[];

  const calculateStackedWidth = (children: ColumnElement[]): number => {
    let child_index = index;
    return children.reduce((acc, child) => {
      if (child.props.children) {
        const array = Children.toArray(child.props.children) as ColumnElement[];
        const width = calculateStackedWidth(array);
        return acc + width;
      }
      child_index++;
      const col_width = column_widths[child_index - 1];
      return acc + col_width;
    }, 0);
  };

  const width = calculateStackedWidth(children);
  const depth_clamp = Math.max(header_clamp / depth, 1);

  const style = cellCSS({
    height: header_height,
    depth,
    fixed,
    index,
    width,
    filler_required,
    cumulative_column_widths,
    cumulative_fixed_left_widths,
    cumulative_fixed_right_widths,
  });

  const first_left_fixed =
    fixed === 'right' &&
    cumulative_fixed_right_widths[index - 1] === cumulative_fixed_right_widths[index];
  const array = Children.toArray(children) as ColumnElement[];
  return (
    <div
      key={`stacked-header-${index}`}
      className={classNames('column', cn.stacked, { [cn.fixed]: first_left_fixed })}
      style={{ ...style, borderColor: theme.header.border_color }}
    >
      <GridHeaderElement
        className={cn.header}
        theme={theme}
        style={{ height: header_height, width: `${width}px` }}
      >
        <p
          className={cn.label}
          style={{
            lineClamp: depth_clamp,
            WebkitLineClamp: depth_clamp,
            lineHeight: `${GRID_LINE_HEIGHT}em`,
            maxHeight: `${GRID_LINE_HEIGHT * depth_clamp}em`,
          }}
        >
          {label}
        </p>
      </GridHeaderElement>
      <div className={cn.container}>
        {array.map((c, idx) => {
          return (
            <GridHeaderLabel
              key={`stacked-header-${index}-${idx}`}
              column={c}
              index={index + idx}
              style={{
                position: 'relative',
                height: `${header_height}px`,
                width: `${column_widths[index + idx]}px`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
